import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getMessageSenders,
  getMessagingConversation,
  getMessagingConversations,
  getThreadMatchCandidates,
  linkConversation,
  markConversationRead,
  sendMessage,
} from "../../services/messaging";
import type {
  MessageSender,
  MessagingContactType,
  MessagingConversation,
  MessagingMatchCandidate,
  MessagingMessage,
} from "../../types/messaging";
import { TablePagination } from "../../components/TablePagination";
import { ComposeMessageModal } from "./ComposeMessageModal";
import "./MessagingInboxPage.css";

const PAGE_SIZE = 20;
const THREAD_PAGE_SIZE = 50;
const REFRESH_MS = 30000;

const CONTACT_LABELS: Record<MessagingContactType, string> = {
  Prospect: "Player",
  Guardian: "Parent",
  // A directory contact is an outside coach or scout, not anyone on the roster — the inbox says
  // so rather than leaving the row looking like a player's.
  TrustedContact: "Contact",
};

type InboxFilter = "all" | "unread" | "unmatched";

const formatRelativeTime = (value: string): string => {
  const diffMins = Math.floor((Date.now() - new Date(value).getTime()) / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

const contactLabel = (conversation: MessagingConversation): string =>
  conversation.contactType ? CONTACT_LABELS[conversation.contactType] : "Unmatched";

/**
 * The Messaging inbox — a texting app, not a CRM list.
 *
 * Messaging keeps its own threads and messages, so this is the only place SMS is
 * listed and there is only ever one place to miss a reply. That includes
 * unmatched threads: a reply from a number no player record claims is shown here
 * with a picker to attach it, rather than sitting in a queue somewhere else.
 */
export const MessagingInboxPage = () => {
  const [conversations, setConversations] = useState<MessagingConversation[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState<InboxFilter>("all");
  const [composeOpen, setComposeOpen] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [selected, setSelected] = useState<MessagingConversation | null>(null);
  const [thread, setThread] = useState<MessagingMessage[]>([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [threadError, setThreadError] = useState("");

  const [candidates, setCandidates] = useState<MessagingMatchCandidate[]>([]);
  const [linkError, setLinkError] = useState("");

  const [senders, setSenders] = useState<MessageSender[]>([]);
  const [replyBody, setReplyBody] = useState("");
  const [replyError, setReplyError] = useState("");
  const [sending, setSending] = useState(false);

  const loadList = useCallback(
    async (targetPage: number, activeFilter: InboxFilter, silent = false) => {
      if (!silent) {
        setListLoading(true);
        setListError("");
      }
      try {
        const data = await getMessagingConversations(
          targetPage,
          PAGE_SIZE,
          activeFilter === "unread",
          activeFilter === "unmatched"
        );
        setConversations(data.items);
        setTotalCount(data.totalCount);
        setPage(targetPage);
      } catch {
        setListError("Failed to load conversations.");
      } finally {
        if (!silent) setListLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    loadList(1, filter);
  }, [loadList, filter]);

  // Only org-number threads can be replied to from here, so the picker is loaded once and
  // reused. A coach with no assigned number gets a read-only inbox rather than a broken box.
  useEffect(() => {
    let cancelled = false;
    getMessageSenders()
      .then((loaded) => {
        if (!cancelled) setSenders(loaded);
      })
      .catch(() => {
        if (!cancelled) setSenders([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll for new replies the way a texting app would. Silent so it never flashes the list.
  const pollRef = useRef({ page, filter });
  pollRef.current = { page, filter };
  useEffect(() => {
    const interval = setInterval(() => {
      loadList(pollRef.current.page, pollRef.current.filter, true);
    }, REFRESH_MS);
    return () => clearInterval(interval);
  }, [loadList]);

  const replyableSenders = useMemo(
    () => senders.filter((s) => s.supportsReplies && s.isAvailable && s.phoneNumberId),
    [senders]
  );

  /**
   * Reply from the number this conversation is already on, not just the first one the coach
   * holds. Threads key on (number, counterparty), so answering from a different number opens a
   * second thread on the recipient's phone from what reads to them as a stranger.
   */
  const replySender = useMemo(() => {
    const threadNumber = selected?.fromNumber;
    return (
      replyableSenders.find((s) => s.phoneNumber === threadNumber) ?? replyableSenders[0] ?? null
    );
  }, [selected, replyableSenders]);

  // Clicking down a list faster than the requests return would otherwise leave an earlier
  // thread rendered under a later conversation's header. Only the newest open wins.
  const openTokenRef = useRef(0);

  const openConversation = useCallback(async (conversation: MessagingConversation) => {
    const token = ++openTokenRef.current;
    setSelected(conversation);
    setThread([]);
    setReplyBody("");
    setReplyError("");
    setLinkError("");
    setCandidates([]);
    setThreadLoading(true);
    setThreadError("");
    try {
      const data = await getMessagingConversation(
        conversation.threadId,
        1,
        THREAD_PAGE_SIZE
      );
      if (token !== openTokenRef.current) return;
      // The endpoint returns newest first so page 1 is the tail of the thread; a reading order
      // of oldest → newest is what makes it look like Messages.
      setThread([...data.items].reverse());
    } catch {
      if (token !== openTokenRef.current) return;
      setThreadError("Failed to load this conversation.");
      setThreadLoading(false);
      return;
    }
    setThreadLoading(false);

    // Offer the link picker for an unmatched thread. Several candidates is the sibling case, and
    // the coach chooses — the API deliberately refuses to guess between them.
    if (conversation.isUnmatched) {
      try {
        const found = await getThreadMatchCandidates(conversation.threadId);
        if (token === openTokenRef.current) setCandidates(found);
      } catch {
        if (token === openTokenRef.current) setCandidates([]);
      }
    }

    if (conversation.unreadCount > 0) {
      try {
        await markConversationRead(conversation.threadId);
        setConversations((prev) =>
          prev.map((c) =>
            c.threadId === conversation.threadId ? { ...c, unreadCount: 0 } : c
          )
        );
      } catch {
        // Read state is not worth an error banner — the next poll corrects it.
      }
    }
  }, []);

  const handleLink = async (candidate: MessagingMatchCandidate) => {
    if (!selected) return;
    setLinkError("");
    try {
      await linkConversation(selected.threadId, {
        contactId: candidate.contactId,
        contactType: candidate.contactType,
      });
      const linked: MessagingConversation = {
        ...selected,
        contactId: candidate.contactId,
        contactType: candidate.contactType,
        contactName: candidate.displayName,
        isUnmatched: false,
      };
      setSelected(linked);
      setCandidates([]);
      await loadList(page, filter, true);
    } catch {
      setLinkError("Could not link this conversation.");
    }
  };

  const handleReply = async () => {
    if (!selected || !replyBody.trim()) return;

    if (!selected.contactId || !selected.contactType) {
      setReplyError("Link this conversation to a player before replying.");
      return;
    }

    const sender = replySender;
    if (!sender?.phoneNumberId) {
      setReplyError("You have no org number assigned, so replies can't be sent from here.");
      return;
    }

    setSending(true);
    setReplyError("");
    try {
      const result = await sendMessage({
        recipient: { contactId: selected.contactId, contactType: selected.contactType },
        body: replyBody,
        phoneNumberId: sender.phoneNumberId,
      });
      if (!result.sent) {
        setReplyError(result.error ?? "The message could not be sent.");
        return;
      }
      setReplyBody("");
      await openConversation({ ...selected, unreadCount: 0 });
      await loadList(page, filter, true);
    } catch {
      setReplyError("The message could not be sent.");
    } finally {
      setSending(false);
    }
  };

  const renderList = () => {
    if (listLoading) return <div className="messaging-inbox__empty">Loading…</div>;
    if (listError) return <div className="messaging-inbox__error">{listError}</div>;
    if (conversations.length === 0) {
      return (
        <div className="messaging-inbox__empty">
          {filter === "unread" && "All caught up."}
          {filter === "unmatched" && "Nothing waiting to be matched."}
          {filter === "all" && "No conversations yet."}
        </div>
      );
    }
    return (
      <>
        <div className="messaging-inbox__list">
          {conversations.map((conversation) => {
            const isSelected = selected?.threadId === conversation.threadId;
            return (
              <button
                key={conversation.threadId}
                type="button"
                className={`messaging-inbox__row${
                  conversation.unreadCount > 0 ? " messaging-inbox__row--unread" : ""
                }${isSelected ? " messaging-inbox__row--selected" : ""}`}
                aria-current={isSelected}
                onClick={() => openConversation(conversation)}
              >
                <div className="messaging-inbox__row-top">
                  <span className="messaging-inbox__row-name">
                    {contactLabel(conversation)} · {conversation.contactName}
                  </span>
                  <span className="messaging-inbox__row-time">
                    {formatRelativeTime(conversation.lastMessageAt)}
                  </span>
                </div>
                {conversation.lastMessageSnippet && (
                  <div className="messaging-inbox__row-snippet">
                    {conversation.lastMessageDirection === "Outbound" && "You: "}
                    {conversation.lastMessageSnippet}
                  </div>
                )}
                {conversation.unreadCount > 0 && (
                  <span className="messaging-inbox__badge">{conversation.unreadCount}</span>
                )}
              </button>
            );
          })}
        </div>
        <TablePagination
          currentPage={page}
          pageSize={PAGE_SIZE}
          totalCount={totalCount}
          onPageChange={(next) => loadList(next, filter)}
        />
      </>
    );
  };

  const renderLinkPicker = () => {
    if (!selected?.isUnmatched) return null;

    return (
      <div className="messaging-inbox__link">
        <p className="messaging-inbox__link-prompt">
          {candidates.length === 0
            ? `No player on your roster has ${selected.counterpartyPhone}. Add the number to a player's record to link this conversation.`
            : "Which player is this?"}
        </p>
        {candidates.map((candidate) => (
          <button
            key={`${candidate.contactType}:${candidate.contactId}`}
            type="button"
            className="messaging-inbox__link-option"
            onClick={() => handleLink(candidate)}
          >
            {candidate.displayName} · {CONTACT_LABELS[candidate.contactType]}
          </button>
        ))}
        {linkError && <div className="messaging-inbox__error">{linkError}</div>}
      </div>
    );
  };

  const renderThread = () => {
    if (!selected) {
      return (
        <div className="messaging-inbox__empty">Pick a conversation to read it.</div>
      );
    }
    if (threadLoading) return <div className="messaging-inbox__empty">Loading…</div>;
    if (threadError) return <div className="messaging-inbox__error">{threadError}</div>;

    // The thread is every message with this person, across every number they were texted on.
    const spansMultipleNumbers = selected.fromNumbers.length > 1;

    return (
      <>
        <div className="messaging-inbox__thread-header">
          <span className="messaging-inbox__thread-name">{selected.contactName}</span>
          <span className="messaging-inbox__thread-kind">{contactLabel(selected)}</span>
        </div>

        {spansMultipleNumbers && (
          <p className="messaging-inbox__thread-numbers">
            This person has been texted from {selected.fromNumbers.join(", ")}. Everything is shown
            together, and a reply goes out from {selected.fromNumber}.
          </p>
        )}

        {renderLinkPicker()}

        <div className="messaging-inbox__messages">
          {thread.map((message) => (
            <div
              key={message.id}
              className={`messaging-inbox__bubble messaging-inbox__bubble--${
                message.direction === "Outbound" ? "out" : "in"
              }`}
            >
              <div className="messaging-inbox__bubble-body">{message.body}</div>
              <div className="messaging-inbox__bubble-meta">
                {message.direction === "Outbound" && message.sentByName && (
                  <span>{message.sentByName}</span>
                )}
                <span>{formatRelativeTime(message.occurredAt)}</span>
                {/* Which line this went out on. Only worth showing once the conversation spans
                    more than one number, which is the case the merged view creates: otherwise it
                    is the same number on every bubble and pure noise. */}
                {spansMultipleNumbers && message.fromNumber && (
                  <span>via {message.fromNumber}</span>
                )}
                {/* A personal-phone send is logged as attempted and never claims delivery — the
                    composer closing is not a receipt. */}
                {message.channel === "PersonalSms" && <span>personal phone</span>}
                {message.deliveryStatus && <span>{message.deliveryStatus.toLowerCase()}</span>}
                {message.deliveryError && (
                  <span className="messaging-inbox__bubble-error">{message.deliveryError}</span>
                )}
              </div>
            </div>
          ))}
          {thread.length === 0 && (
            <div className="messaging-inbox__empty">No messages in this conversation.</div>
          )}
        </div>

        {replySender && !selected.isUnmatched ? (
          <div className="messaging-inbox__composer">
            <label className="messaging-inbox__composer-label" htmlFor="messaging-reply">
              Reply from {replySender.label}
            </label>
            <textarea
              id="messaging-reply"
              className="messaging-inbox__composer-input"
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              rows={3}
              placeholder="Write a reply…"
            />
            {replyError && <div className="messaging-inbox__error">{replyError}</div>}
            <button
              type="button"
              className="messaging-inbox__send-btn"
              onClick={handleReply}
              disabled={sending || !replyBody.trim()}
            >
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        ) : (
          <div className="messaging-inbox__composer-disabled">
            {selected.isUnmatched
              ? "Link this conversation to a player before replying."
              : "Replying needs an org number assigned to you. Personal-phone threads are one-way — replies to them go to your own Messages app."}
          </div>
        )}
      </>
    );
  };

  const renderFilter = (value: InboxFilter, label: string) => (
    <button
      type="button"
      className={`messaging-inbox__filter${
        filter === value ? " messaging-inbox__filter--active" : ""
      }`}
      onClick={() => setFilter(value)}
    >
      {label}
    </button>
  );

  return (
    <div className="messaging-inbox">
      <div className="messaging-inbox__toolbar">
        <div className="messaging-inbox__filters">
          {renderFilter("all", "All")}
          {renderFilter("unread", "Unread")}
          {renderFilter("unmatched", "Unmatched")}
        </div>
        <button
          type="button"
          className="messaging-inbox__compose-btn"
          onClick={() => setComposeOpen(true)}
        >
          New message
        </button>
      </div>

      {/* The spec is explicit that this has to be said out loud rather than assumed: an
          org-assigned number carries no privacy expectation, and whoever holds it next reads
          everything on it. It is also the honest reason the personal-phone option exists. */}
      <p className="messaging-inbox__disclosure">
        Conversations on org numbers are visible to your whole staff, now and to whoever holds
        the number next. Use your own phone for anything that shouldn't be.
      </p>

      <div className="messaging-inbox__panes">
        <div className="messaging-inbox__pane messaging-inbox__pane--list">{renderList()}</div>
        <div className="messaging-inbox__pane messaging-inbox__pane--thread">
          {renderThread()}
        </div>
      </div>

      <ComposeMessageModal
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSent={() => {
          // A send creates or touches a thread, so the list behind the modal is now stale.
          // Silent: the rows update in place rather than flashing a spinner over them.
          void loadList(page, filter, true);
        }}
      />
    </div>
  );
};
